import { HelpBtn } from './HelpBtn';
import MaskToggle from './MaskToggle';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, Ruler, Crosshair, ChevronLeft } from 'lucide-react';
import { arcFit, rayPoint, distM, svyRefLabel, svyTargetLabel, isBendTarget } from '../lib/survey';
import { resolvePt } from '../lib/model';
import { SurveyPhotoBtns, SurveyPhotoList } from './SurveyInfo';

export const openSurvey = (target) => window.dispatchEvent(new CustomEvent('giss-survey', { detail: target }));
const num = (v) => +String(v).replace(',', '.');

// guided crosshair-only survey wizard (1 point = distance, 2 points = arc intersection)
export default function SurveyDialog({ t, project, net, mapApi }) {
  const [tg, setTg] = useState(null);
  const [f, setF] = useState(null);
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const pos = () => {
    if (!tg) return null;
    if (tg.kind === 'fitting') { const x = project.fittings.find((y) => y.id === tg.id); return x ? [x.lat, x.lon] : null; }
    const p = project.pipes.find((y) => y.id === tg.pipeId);
    return p ? resolvePt(p.points[tg.i], project.fittings) : null;
  };
  useEffect(() => { document.body.classList.toggle('svy-active', !!tg); }, [tg]);
  useEffect(() => { mapApi.current?.svyMarks(tg && f ? [f.a, f.b].filter(Boolean) : []); }, [tg, f, mapApi]);
  useEffect(() => {
    const on = (e) => {
      const g = e.detail;
      const cur = g.kind === 'fitting' ? project.fittings.find((x) => x.id === g.id) : project.pipes.find((x) => x.id === g.pipeId);
      const s = g.kind === 'fitting' ? cur?.survey : cur?.surveys?.[g.i];
      setTg(g);
      setF({ kindRef: s?.kindRef || 'house', n: s?.n || 2, a: null, b: null, da: s?.da ?? '', db: s?.db ?? '', photos: s?.photos || [] });
    };
    window.addEventListener('giss-survey', on);
    return () => window.removeEventListener('giss-survey', on);
  }, [project]);
  if (!tg || !f) return null;
  const cc = () => { const c = mapApi.current.getCenter(); return [c.lat, c.lng]; };
  const two = f.n === 2;
  const refLabel = svyRefLabel(f.kindRef, t);
  const target = svyTargetLabel(t, tg, project);
  const close = () => setTg(null);
  const done = (r, meth, approx) => {
    if (!r || r.some(Number.isNaN)) return toast.error(t.svyNoSolution);
    const ref = f.kindRef === 'gg';
    const sv = { kindRef: f.kindRef, n: f.n, a: f.a, b: two ? f.b : null, method: meth, da: num(f.da), db: two ? num(f.db) : null, approx: !!approx, photos: f.photos, labelA: ref ? t.fit_gg : t.svyCornerA, labelB: ref ? t.fit_gg : t.svyCornerB, at: Date.now() };
    if (tg.kind === 'fitting') net.updateFitting(tg.id, { lat: r[0], lon: r[1], survey: sv });
    else { net.moveVertex(tg.pipeId, tg.i, r); net.updatePipe(tg.pipeId, (p) => ({ surveys: { ...(p.surveys || {}), [tg.i]: sv } })); }
    mapApi.current.setView(r[0], r[1]);
    if (approx) toast.warning(t.svyApprox); else toast.success(t.svyApplied);
    close();
  };
  const apply = () => {
    if (!(num(f.da) > 0) || (two && !(num(f.db) > 0))) return toast.error(t.svyNeedDist);
    if (two) { const r = arcFit(f.a, f.b, num(f.da), num(f.db), pos() || cc()); return done(r.pt, 'arc', r.approx); }
    const p = pos(), dir = p && distM(f.a, p) >= 0.3 ? p : cc();
    if (distM(f.a, dir) < 0.3) return toast.error(t.svyNeedDir);
    done(rayPoint(f.a, dir, num(f.da)), 'ray');
  };
  const step = !f.a ? 1 : two && !f.b ? 2 : 3;
  const total = two ? 3 : 2;
  const back = () => set(step === 3 && two ? { b: null } : { a: null, b: null });
  const chip = (k, v, l) => <button className={`chip seg ${f[k] === v ? 'on' : ''}`} data-testid={`svy-${k}-${v}`} onClick={() => set({ [k]: v, a: null, b: null })}>{l}</button>;
  const inp = (k, ph) => <input type="text" inputMode="decimal" data-testid={`survey-${k}-input`} placeholder={ph} value={f[k]} onChange={(e) => set({ [k]: e.target.value })} />;
  const hint = step === 1 ? t.svyStep1 : step === 2 ? t.svyStep2 : two ? t.svyStep3b : t.svyStep3a;
  return (
    <div className="svy-wizard mask" data-testid="survey-dialog">
      <div className="svy-w-head mask-keep">
        <b>📐 {t.svyTitle}</b>
        <span className="svy-w-step" data-testid="survey-step-indicator">{t.svyStepLbl} {two ? step : Math.min(step, 2)}/{total}</span>
        <MaskToggle t={t} id="survey" />
        <HelpBtn k="n:meas_survey" testid="survey-help" title={t.helpHere} /><button className="icon-btn" data-testid="survey-close-btn" onClick={close}><X size={16} /></button>
      </div>
      {isBendTarget(tg, project) && <p className="svy-w-bend" data-testid="survey-bend-hint">{t.svyBendHint}</p>}
      {step === 1 && <div className="svy-w-row">{chip('kindRef', 'house', `🧱 ${t.svyRefHouse}`)}{chip('kindRef', 'gg', `🚧 ${t.fit_gg}`)}{chip('n', 1, t.svyN1)}{chip('n', 2, t.svyN2)}</div>}
      <p className="svy-w-hint" data-testid="survey-step-hint">{hint}</p>
      <div className="svy-w-row">
        {step > 1 && <button className="btn btn-outline btn-sm" data-testid="survey-back-btn" onClick={back}><ChevronLeft size={15} /> {t.svyBack}</button>}
        {step === 1 && <button className="btn btn-green" data-testid="survey-confirm-p1-btn" onClick={() => set({ a: cc() })}><Crosshair size={16} /> {t.svyConfirmP1}</button>}
        {step === 2 && <button className="btn btn-green" data-testid="survey-confirm-p2-btn" onClick={() => { const b = cc(); if (distM(f.a, b) < 0.3) return toast.error(t.svySamePt); set({ b }); }}><Crosshair size={16} /> {t.svyConfirmP2}</button>}
        {step === 3 && (
          <>
            <label className="svy-w-in">{two ? t.svyDistA : t.svyDist} (m){inp('da', '2,00')}</label>
            {two && <label className="svy-w-in">{t.svyDistB} (m){inp('db', '3,00')}</label>}
            <button className="btn btn-green" data-testid="survey-apply-btn" onClick={apply}><Ruler size={16} /> {t.svyConfirmSet}</button>
          </>
        )}
      </div>
      {step === 3 && (
        <>
          <p className="svy-w-sub" data-testid="survey-photo-howto">📷 {t.svpHowTo}</p>
          <div className="svy-w-row">
            <SurveyPhotoBtns t={t} two={two} dists={{ a: f.da, b: f.db }} refLabel={refLabel} target={target} prefix="survey" onAdd={(p) => setF((x) => ({ ...x, photos: [...x.photos, p] }))} />
          </div>
          <SurveyPhotoList t={t} photos={f.photos} prefix="survey-wiz" onDelete={(id) => set({ photos: f.photos.filter((x) => x.id !== id) })} />
        </>
      )}
    </div>
  );
}
